// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## NoteHolder
//
// An object to hold and sort multiple notes.
//
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { isNumber } from 'shared/src/utils/isNumber';
import type { GhostNote, StaveNoteStruct } from 'vexflow';
import { Dot } from 'vexflow';
import { StaveNote, Accidental } from 'vexflow';
import Rhythm from './rhythm';
import type { AriaStringOptions } from './note';
import Note from './note';
import Chord from './chord';
import utils from '../utils';
import Annotation from '../uTheory.Vex/annotation';
import MusicObject from './musicObject';
import Clef from './clef';
import Interval from './interval';
import type { MidiInfoObject, MidiMessage } from './soundBufferHolder';
import type Editable from './editable';
import type KeySignature from './keySignature';
import type CustomKeySignature from './customKeySignature';
import config from '../config';

type StemDirection = -1 | 0 | 1;

const noteHolderStringProps = ['velocity', 'staccato', 'color', 'isPickup', 'annotation'] as const;

const noteHolderStringPropsToAbbreviation: Record<typeof noteHolderStringProps[number], string> = {
  velocity: 'v',
  staccato: 's',
  color: 'c',
  isPickup: 'p',
  annotation: 't',
} as const;

/* Acceptable constructs:
  NoteHolder([noteOrNoteString, noteOr...], (optional rhythm or rhythmString) );
  NoteHolder(note, (optional rhythm or rhythmString) );
*/
export default class NoteHolder extends MusicObject {
  notes: Note[];

  /** Is this a committed rest? (As opposed to just empty...) */
  rest = false;

  isPickup: boolean;

  rhythm?: Rhythm;

  editable?: Editable;

  velocity?: number;

  staccato?: number;

  // private _color: string | undefined;

  // get color(): string | undefined {
  //   return this._color;
  // }

  // set color(color: string) {
  //   if (color === 'undefined') {
  //     console.error(Error('Setting noteHolder color as "undefined"'));
  //   }
  //   this._color = color;
  // }

  color?: string;

  strokeColor?: string;

  annotation?: Annotation;

  /** StaveNote, applied after drawing */
  staveNote?: StaveNote | GhostNote;

  /** Don't center whole note rest */
  doNotCenter: boolean = false;

  /**
   * If there's a partial beam, which side should it be on? Good
   * defaults are calculated, but in cases like 6/8 e. s e, you
   * might want the s beam going right, if it's locally acting
   * as a 12/16 bar
   */
  forcePartialBeamDirection?: 'left' | 'right';

  /**
   * Creates a new NoteHolder object, if a string of notes are inputted,
   * separate by commas or space
   */
  // Public constructor
  constructor();
  constructor(note: Note, rhythm?: Rhythm | string | number);
  constructor(noteArray: Note[] | NoteName[], rhythm?: Rhythm | string | number);
  constructor(notes: NoteName, rhythm?: Rhythm | string | number);
  constructor(
    noteUndefinedOrArray?: Note | NoteName | Note[] | NoteName[] | '',
    rhythm?: Rhythm | string | number
  );

  constructor(
    noteUndefinedOrArray?: Note | NoteName | Note[] | NoteName[] | '',
    rhythm?: Rhythm | string | number
  ) {
    super();
    this.notes = [];
    this.isPickup = false;

    // if rhythm exists:
    if (typeof rhythm !== 'undefined') {
      if (rhythm instanceof Rhythm) {
        this.rhythm = rhythm;
      } else this.rhythm = new Rhythm(rhythm);
    }

    // if it's undefined, leave this all empty
    if (typeof noteUndefinedOrArray === 'undefined') return this;

    if (typeof noteUndefinedOrArray === 'string') {
      // if it's an empty string, return this;
      if (noteUndefinedOrArray.length === 0) return this;

      // if it's a string, it can be comma sepearated, let's make it into an array
      noteUndefinedOrArray = noteUndefinedOrArray.replace(/ /g, '').split(',') as NoteName[];
    }

    if (noteUndefinedOrArray instanceof Array) {
      // foreach, if it's a note, we add it
      // if it's not, we assume it's a string note name and create that note.
      noteUndefinedOrArray.forEach((note) => {
        this.notes.push(NoteHolder.stringToNote(note));
      });

      return this;
    }

    this.notes.push(NoteHolder.stringToNote(noteUndefinedOrArray));
    return this;
  }

  /**
   * Get how many notes are in this NoteHolder
   */
  get length() {
    return this.notes.length;
  }

  /**
   * Set how many notes will be in this NoteHolder
   */
  set length(len) {
    this.notes.length = len;
    if (len === 0 && this.editable) {
      this.editable.selectedNotes = [];
    }
  }

  private _stemDirection: StemDirection = 0;

  /**
   * -1 = stem down;
   * 0 = auto;
   * 1 = stem up;
   */
  get stemDirection(): StemDirection {
    return this._stemDirection || 0;
  }

  /**
   * @param {number} direction 1 (up), 0 (auto), -1 (down);
   */
  set stemDirection(direction: StemDirection) {
    if (utils.typeof(direction) !== 'number') throw new Error('Stem direction must be 1, 0, or -1');
    if (direction > 1 || direction < -1) throw new Error('Stem direction must be 1, 0, or -1');
    if (direction % 1 !== 0)
      throw new Error(`Stem direction must be an integer -1, 0, or 1, and not ${direction}`);
    this._stemDirection = direction as StemDirection;
  }

  /**
   * Provides a quick way of accessing each note without having to
   * refer directly to the notes variable from outside.
   */
  forEach(callback: (value: Note, index: number, array: Note[]) => void, thisArg?: this) {
    this.notes.forEach(callback, thisArg);
    return this;
  }

  /**
   * Add a note to the NoteHolder and return the index of the last note
   */
  push(note: Note | NoteName) {
    this.notes.push(NoteHolder.stringToNote(note));
    return this.notes.length - 1;
  }

  /**
   * Empties noteHolder of any note
   */
  clearNotes() {
    this.length = 0;
  }

  /**
   * Replaces a specified note or index with another note
   */
  replace(note: Note, newNote: NoteName | Note): this;
  replace(noteName: NoteName, newNote: NoteName | Note): this;
  replace(index: number, newNote: NoteName | Note): this;
  replace(indexNoteOrString: NoteName | Note | number, newNote: Note | NoteName) {
    let index = this.indexFromNoteOrString(indexNoteOrString);
    if (index > this.notes.length - 1) index = this.notes.length;
    if (index !== false) this.notes[index] = NoteHolder.stringToNote(newNote);
    return this;
  }

  /**
   * Remove a specified index or note once
   */
  remove(noteName: NoteName): this;
  remove(note: Note): this;
  remove(index: number): this;
  remove(indexNoteOrString: number | Note | NoteName) {
    // Sanity check: some older code has called this with undefined in the
    // past:
    if (typeof indexNoteOrString === 'undefined') return this;

    const index = this.indexFromNoteOrString(indexNoteOrString);
    if (index !== false) this.notes.splice(index, 1);
    return this;
  }

  /**
   * Concatenate two note holders into a new big one
   */
  concat(that: NoteName[] | Note[] | NoteName | NoteHolder): NoteHolder {
    if (!(that instanceof NoteHolder)) that = new NoteHolder(that);
    const nh = this.clone();
    nh.notes = nh.notes.concat(that.notes);
    return nh;
  }

  transpose(interval: Interval | IntervalName) {
    if (!(interval instanceof Interval)) {
      interval = new Interval(interval);
    }

    this.notes.forEach((note) => note.transpose(interval));
  }

  /**
   * Returns the index of this note. If already a string index, convert to
   * number than return
   */
  private indexFromNoteOrString(indexNoteOrNoteName: Note | NoteName | number) {
    if (utils.isNumeric(indexNoteOrNoteName) || isNumber(indexNoteOrNoteName)) {
      return Number(indexNoteOrNoteName);
    }
    return this.getIndex(indexNoteOrNoteName);
  }

  /**
   * Returns the index of a note, false if note is not found
   */
  getIndex(note: Note | NoteName): number | false {
    note = Note.fromStringOrNote(note);
    const index = this.notes.findIndex((haystackNote) => haystackNote.equals(note));
    return index !== -1 ? index : false;
  }

  /**
   * Check if note is in this NoteHolder, if yes, return its index as well
   */
  noteExistsAt(note: Note | NoteName): false | [true, number] {
    note = Note.fromStringOrNote(note);
    const i = this.getIndex(note);
    if (i === false) return false;
    return [true, i];
  }

  /**
   * Check if note is in this NoteHolder, if yes, returns the note with accidental and index only
   */
  noteExistsAtIgnoreAccidental(note: Note | NoteName): false | [Note, number] {
    note = Note.fromStringOrNote(note);
    for (let i = 0; i < this.notes.length; i++) {
      if (this.notes[i]!.equalsIgnoreAccidental(note)) return [this.notes[i]!, i];
    }
    return false;
  }

  /**
   * Check if note is in this NoteHolder ignoring octave, if yes, return its index as well
   */
  noteExistsAtIgnoreOctave(note: Note | NoteName): false | [boolean, number] {
    note = Note.fromStringOrNote(note);
    for (let i = 0; i < this.notes.length; i++) {
      if (this.notes[i]!.equalsIgnoreOctave(note)) return [true, i];
    }
    return false;
  }

  /**
   * Gets the nearest note in this NoteHolder to the specified note. May be
   * undefined (if, for instance, note holder is empty)
   */
  getNearestNote(note: Note | NoteName, roundUp?: boolean): Note | undefined {
    note = Note.fromStringOrNote(note);
    const orderedNotes = this.getNearestNotes(note, roundUp);
    return orderedNotes[0]?.note;
  }

  /**
   * Gets an array of notes in this NoteHolder sorted by distance to the specified note
   */
  getNearestNotes(
    noteOrNoteName: NoteName | Note,
    roundUp?: boolean
  ): { note: Note; index: number; generic: number }[] {
    const note = Note.fromStringOrNote(noteOrNoteName);
    roundUp = typeof roundUp === 'undefined' ? true : roundUp;
    if (this.isEmpty()) return [];
    return this.notes
      .map((haystackNote, i) => ({
        note: haystackNote,
        index: i,
        generic: note.getGenericIntervalTo(haystackNote),
      }))
      .sort((a, b) => {
        const aAbs = Math.abs(a.generic);
        const bAbs = Math.abs(b.generic);
        if (aAbs < bAbs) return -1;
        if (bAbs < aAbs) return 1;
        // eslint-disable-next-line no-nested-ternary
        return roundUp ? (a.generic > b.generic ? -1 : 1) : a.generic > b.generic ? 1 : -1;
      });
  }

  /** Is the noteHolder empty of notes? (It might still be a rest...) */
  isEmpty() {
    return this.notes.length === 0;
  }

  /** Is the noteHolder neither a note nor a rest? */
  isNeitherNoteNorRest() {
    return this.length === 0 && !this.rest;
  }

  get rendersAs(): 'note' | 'rest' | 'pendingRest' | 'placeHolderRest' | 'ghostNote' {
    if (this.notes.length || (this.editable && this.editable.pendingNote)) {
      return 'note';
    }
    if (this.rest) return 'rest';
    if (this.editable && this.editable.shouldRenderAsRestBecauseLinkedIsFull()) return 'rest';
    if (this.editable && this.editable.pendingRest) return 'pendingRest';
    if (this.editable && this.editable.placeHolderRest) return 'placeHolderRest';
    return 'ghostNote';
  }

  isRest() {
    return ['rest', 'pendingRest', 'placeHolderRest'].includes(this.rendersAs);
  }

  isBeamable() {
    return (
      // // Isn't empty:
      !this.isEmpty() &&
      // Has rhythm:
      typeof this.rhythm !== 'undefined' &&
      // Rhythm is beamable:
      this.rhythm.isBeamable()
    );
  }

  /**
   * Number of beams when beamed together; rests are always 1 because they appear
   * with flags and stemlets, rather than actually as beams. Noteholders without
   * rhythm can't be beamed, so, they have 0 beams.
   */
  numberOfBeams() {
    if (!this.rhythm) return 0;
    return this.isBeamable() && (this.isRest() ? 1 : this.rhythm.numberOfBeams());
  }

  /**
   * Is this noteHolder in its most compact form?
   */
  isCloseSpacing(): boolean {
    return this.clone().sortAndCompact().equals(this.clone().sort());
  }

  /**
   * Is this noteHolder in an open spacing? (Not its most compact form?)
   * @return {boolean} true if in open spacing
   */
  isOpenSpacing(): boolean {
    return !this.isCloseSpacing();
  }

  equals(
    thatNoteHolderChordNoteNameOrNoteNameArray: Chord | NoteHolder | NoteName | NoteName[] | Note[]
  ): boolean {
    // If it's a chord, run a chord comparison:
    if (thatNoteHolderChordNoteNameOrNoteNameArray instanceof Chord) {
      return thatNoteHolderChordNoteNameOrNoteNameArray.equals(this);
    }

    const noteHolder =
      thatNoteHolderChordNoteNameOrNoteNameArray instanceof NoteHolder
        ? thatNoteHolderChordNoteNameOrNoteNameArray
        : new NoteHolder(thatNoteHolderChordNoteNameOrNoteNameArray);

    // Makes certain both noteholders have the same notes in them -- though
    // they need not be sorted in the same way.
    if (this.notes.length !== noteHolder.notes.length) return false;
    return this.notes.every((note) => noteHolder.noteExistsAt(note));
  }

  equalsIgnoreOctave(thatNoteHolder: Chord | NoteName | NoteName[] | Note[] | NoteHolder): boolean {
    // If it's a chord, run a chord comparison:
    // We'll ignore inversions as well...
    if (thatNoteHolder instanceof Chord) {
      return thatNoteHolder.equalsIgnoreInversion(this);
    }

    const noteHolder =
      thatNoteHolder instanceof NoteHolder ? thatNoteHolder : new NoteHolder(thatNoteHolder);

    // Otherwise, it's a NoteHolder:
    // be sure all of this.notes are in thatNoteHolder
    const ourNotesMatch = this.notes.every((note) => noteHolder.noteExistsAtIgnoreOctave(note));
    const theirNotesMatch = noteHolder.notes.every((note) => this.noteExistsAtIgnoreOctave(note));
    return ourNotesMatch && theirNotesMatch;
  }

  /**
   * Given an optional array of clefs (default treble & bass) what clefs do these notes
   * display in correctly?
   */
  prefersClef(clefsArray?: (Clef | ClefName)[], maxLedgerLines = 2): ClefName[] {
    clefsArray = clefsArray || ['treble', 'bass'];
    let sharedClefs: ClefName[] = [];
    this.notes.forEach((note, index) => {
      // Get first note's preferred clefs
      if (index === 0) sharedClefs = note.prefersClef(clefsArray, maxLedgerLines);
      // Reduce shared clefs if not note one:
      // use shared clefs to limit more:
      else {
        sharedClefs = note.prefersClef(sharedClefs, maxLedgerLines);
      }
    });

    return sharedClefs;
  }

  /**
   * @param doNotCloneNotes determine whether individual notes are cloned
   */
  clone(doNotCloneNotes: boolean = false) {
    const notes: Note[] = [];
    this.notes.forEach((note) => {
      notes.push(doNotCloneNotes ? note : note.clone());
    });
    const newHolder = new NoteHolder(notes);

    if (this.rhythm) newHolder.rhythm = this.rhythm.clone();
    if (this.annotation) newHolder.annotation = this.annotation.clone();

    newHolder.rest = this.rest;
    newHolder.isPickup = this.isPickup;
    newHolder.velocity = this.velocity;
    newHolder.staccato = this.staccato;
    newHolder.color = this.color;
    newHolder.doNotCenter = this.doNotCenter;

    return newHolder;
  }

  sort() {
    this.notes.sort((note1, note2) => {
      if (note1.isGreaterThan(note2)) return 1;
      if (note2.isGreaterThan(note1)) return -1;
      return 0;
    });
    return this;
  }

  /**
   * @returns {NoteHolder} sorted by letter and octaves ignoring accidental
   */
  sortIgnoreAccidental() {
    this.notes.sort((note1, note2) => {
      if (note1.isGreaterThanIgnoreAccidental(note2)) return 1;
      if (note2.isGreaterThanIgnoreAccidental(note1)) return -1;
      return 0;
    });
    return this;
  }

  sortAndCompact() {
    if (this.isEmpty()) return this;

    this.sort();
    // Pull all notes down into the closest octave

    let i = this.notes.length;
    const comparisonNote = this.notes[0]!.clone();
    comparisonNote.octave++;
    while (i-- > 1) {
      while (this.notes[i]!.isGreaterThanOrEquals(comparisonNote)) {
        this.notes[i]!.octave--;
      }
    }
    this.sort();

    // Delete any duplicates:
    i = this.notes.length;
    while (i-- > 1) {
      if (this.notes[i]!.equals(this.notes[i - 1]!)) {
        this.notes.splice(i, 1);
      }
    }
    return this;
  }

  toAriaString(
    ariaStringOptions: AriaStringOptions = {},
    clef?: Clef,
    keySignature?: KeySignature | CustomKeySignature
  ): string {
    ariaStringOptions = {
      lyricWord: 'lyric',
      ...ariaStringOptions,
    };
    const { rhythm = true, theWordNote = true, selected = true } = ariaStringOptions;
    let text = '';
    // If this is a ghost note -- show nothing.
    if (this.isEmpty() && !this.isRest()) return '';
    // If this is selected, mention it first:
    const isSelected = this.editable?.selected;
    const selectedNotes: false | Note[] = !!this.editable?.selected && this.editable?.selectedNotes;

    if (rhythm && this.rhythm) {
      text +=
        this.rhythm.toAriaString({ rhythm }) +
        (this.isRest() ? '-rest' : theWordNote ? '-note' : '');
    }
    if (this.isRest()) return text || 'rest';
    const notesAriaStrings = this.notes.map((note) => {
      let noteText = '';
      const suppressNoteAccidental =
        ariaStringOptions.lineOrSpace && this.rhythm && this.rhythm.isTied;
      noteText += note.toAriaString(
        { ...ariaStringOptions, accidental: ariaStringOptions && !suppressNoteAccidental },
        clef,
        keySignature
      );
      if (
        selected &&
        selectedNotes &&
        !this.equals(selectedNotes) &&
        selectedNotes.find((selectedNote) => selectedNote.equals(note))
      ) {
        noteText += ' is selected';
      }
      return noteText;
    });
    const notesString = utils.listToString(notesAriaStrings, { and: '&' });
    text += text.length && notesString.length ? ' ' : '';
    text += notesString;
    // If the whole thing is selected, but not individual notes, mention that
    if (selected && isSelected && (!selectedNotes || this.equals(selectedNotes))) {
      text += `${this.notes.length > 1 ? ' are' : ' is'} selected`;
    }
    if (this.annotation && this.annotation.getText())
      text += ` lyric: ${this.annotation.getText()}`;
    return text;
  }

  toString(includeProperties = false) {
    let scratchString = '';
    let spacer = '';
    for (let i = 0; i < this.notes.length; i++) {
      scratchString += spacer + this.notes[i];
      spacer = '-';
    }
    // Adjust for rest or ghost note:
    if (scratchString.length === 0) {
      if (!this.rest) scratchString += '*';
      else scratchString += 'r';
    }

    if (this.rhythm) scratchString += `/${this.rhythm}`;

    if (includeProperties) {
      const propStrings: string[] = [];

      noteHolderStringProps.forEach((prop) => {
        switch (prop) {
          case 'velocity':
            if (this.velocity !== undefined)
              propStrings.push(`${noteHolderStringPropsToAbbreviation[prop]}${this.velocity}`);
            break;
          case 'staccato':
            if (this.staccato !== undefined)
              propStrings.push(`${noteHolderStringPropsToAbbreviation[prop]}${this.staccato}`);
            break;
          case 'color':
            if (this.color !== undefined)
              propStrings.push(`${noteHolderStringPropsToAbbreviation[prop]}${this.color}`);
            break;
          case 'isPickup':
            if (this.isPickup) propStrings.push(`${noteHolderStringPropsToAbbreviation[prop]}`);
            break;
          case 'annotation':
            if (this.annotation) {
              propStrings.push(
                `${noteHolderStringPropsToAbbreviation[prop]}'${this.annotation.getText()}'`
              );
            }
        }
      });

      if (propStrings.length) scratchString += `:${propStrings.join(',')}`;
    }

    return scratchString;
  }

  fromString(string: string) {
    return NoteHolder.fromString(string, this);
  }

  /**
   * Gets an array containing the MIDI number and the start and stop beat of each note in
   * this NoteHolder
   */
  getMidiMessages(midiInfo: MidiInfoObject = { startBeat: 0, stopBeat: 4 }): MidiMessage[] {
    const startBeat = typeof midiInfo.startBeat === 'undefined' ? 0 : midiInfo.startBeat;
    const stopBeat = typeof midiInfo.stopBeat === 'undefined' ? 4 : midiInfo.stopBeat;

    const midiMessages: MidiMessage[] = [];
    this.notes.forEach((note) => {
      const midiNumber = note.getMidiNumber();
      midiMessages.push({
        midiNumber,
        startBeat,
        stopBeat,
      });
    }, this);
    return midiMessages;
  }

  /**
   * Creates a VexFlow StaveNote from the given note holder.
   */
  getStaveNote(
    clef: Clef | string,
    accidentalsHaystack: Note[],
    keySignature: KeySignature | CustomKeySignature,
    rendererOptions = {
      ghostColor: config.Colors.editorColors.ghosting,
      selectedColor: config.Colors.editorColors.selected,
    },
    staveNoteStemDirection?: -1 | 0 | 1
  ): StaveNote {
    if (!this.rhythm) throw Error('Cannot getStaveNote without first setting NoteHolder.rhythm');

    if (clef instanceof Clef) {
      clef = clef.getVexName();
    }

    const baseDuration = this.rhythm.baseDuration === 'b' ? '1/2' : this.rhythm.baseDuration;
    let dots = '';
    for (let dot = 0; dot < this.rhythm.getDots(); dot++) dots += 'd';

    const notes = this.notes.slice(0);
    if (this.editable && this.editable.pendingNote) notes.push(this.editable.pendingNote);
    notes.sort((a, b) => (a.isGreaterThanIgnoreAccidental(b) ? 1 : -1));

    const keys = notes.map((note) => note.toVexString());
    const staveNoteProps: StaveNoteStruct = {
      clef,
      keys,
      duration: `${baseDuration + dots}n`,
    };

    if (staveNoteStemDirection || this.stemDirection) {
      // eslint-disable-next-line camelcase, @typescript-eslint/naming-convention
      staveNoteProps.stem_direction = staveNoteStemDirection || this.stemDirection;
    } else {
      // eslint-disable-next-line camelcase, @typescript-eslint/naming-convention
      staveNoteProps.auto_stem = true;
    }

    const staveNote = new StaveNote(staveNoteProps);

    // @todo(vex4): Verify that dots are still showing for notes. (replaced addDotToAll)
    for (let i = 0; i < this.rhythm.dots; i++) Dot.buildAndAttach([staveNote], { all: true });

    notes.forEach((note, index) => {
      const noteNeedsAccidental = needsAccidental(note, accidentalsHaystack, this, keySignature);
      if (noteNeedsAccidental) {
        const accidental = note.toVexAccidental();
        const vexAccidental = new Accidental(accidental);
        if (noteNeedsAccidental.cautionary) vexAccidental.setAsCautionary();
        staveNote.addModifier(vexAccidental, index);
        // staveNote.addAccidental(index, vexAccidental);
      }
    });

    styleStaveNote(staveNote, this, notes, rendererOptions);

    return staveNote;
  }

  /**
   * Turn a string or a Note object into a Note object:
   */
  static stringToNote(stringOrNote: NoteName | Note) {
    if (stringOrNote instanceof Note) {
      return stringOrNote;
    }
    return new Note(stringOrNote);
  }

  /**
   * Converts a string into a NoteHolder object
   *
   * The format for the string is:
   *   - `{noteName | noteName-noteName-etc... | r (rest) | * (hidden)}{/rhythm?}{:PropsString?}`
   *
   * The format for the propsString is:
   *   - `:{c{Color}?},{v{Velocity}?},{s{Staccato}?},{p? -- is it pickup?},{t"{text}"}
   */
  static fromString(string: string, nhOrMapIndex: NoteHolder | number = new NoteHolder()) {
    // This can happen if fromString is called in Array.map.
    const nh = nhOrMapIndex instanceof NoteHolder ? nhOrMapIndex : new NoteHolder();

    nh.notes = [];
    // Notes can have their own props, so we need to split on the last colon that is NOT
    // in quotation marks
    const propsSplit = string.split(/:+(?=(?:(?:[^']*'){2})*[^']*$)/g);
    const props = propsSplit.length > 1 ? propsSplit.pop() : undefined;
    // And trim it off of the string:
    if (props) string = string.slice(0, -(props.length + 1));

    // Split on a slash
    const parts = string.split('/');
    if (parts.length > 1) nh.rhythm = new Rhythm(parts[1]!);
    const notes = parts[0]!.split('-');
    notes.forEach((note) => {
      if (note === 'r') {
        nh.rest = true; // it's a rest, mark it, don't add anything to notes.
        return;
      }
      if (note === '*') {
        // it's a ghost note, leave empty.
        return;
      }
      nh.notes.push(Note.fromStringOrNote(note));
    });

    // Now read in properties, if we have any:
    if (props) {
      // Split it on commas NOT within text:
      const properties = props.split(/,+(?=(?:(?:[^']*'){2})*[^']*$)/g);

      properties.forEach((prop) => {
        switch (prop[0]) {
          case 'v':
            nh.velocity = Number(prop.slice(1));
            break;
          case 's':
            if (!(prop.indexOf('stem') === 0)) {
              nh.staccato = Number(prop.slice(1));
            }
            break;
          case 'c':
            nh.color = prop.slice(1);
            break;
          case 'p':
            nh.isPickup = true;
            break;
          case 't':
            nh.annotation = new Annotation(prop.slice(2, -1), {
              justification: 'center',
              verticalJustification: 'bottom',
            });
            break;
          default:
            break;
        }

        if (prop.indexOf('stem') === 0) {
          const stemDirectionFromProp = Number(prop.slice(4));
          if ([-1, 0, 1].includes(stemDirectionFromProp)) {
            nh.stemDirection = stemDirectionFromProp as -1 | 0 | 1;
          }
        }
      });
    }
    return nh;
  }
}

/**
 * Does a note need an accidental?
 * @param {Note} note
 * @param {Note[]} haystack of notes that already have accidentals in this measure. If any additional
 * accidentals are drawn, they'll be pushed to this array.
 * @param {NoteHolder} noteHolder object that might need an accidental
 */
function needsAccidental(
  note: Note,
  haystack: Note[],
  noteHolder: NoteHolder,
  keySig: KeySignature | CustomKeySignature
) {
  const inKeySig = keySig.contains(note);
  let inMeasureAccidentals = false;
  let contradictedInMeasureAccidentals = false;

  // eslint-disable-next-line @typescript-eslint/no-shadow
  let needsAccidental = false;
  haystack.forEach((haystackNote, index) => {
    if (note.equals(haystackNote)) inMeasureAccidentals = true;
    if (note.letter === haystackNote.letter && note.accidental !== haystackNote.accidental) {
      contradictedInMeasureAccidentals = true;
      haystack.splice(index, 1);
    }
  });

  // Does this note not need an accidental because it's tied?
  if (noteHolder.rhythm?.isTied) {
    return false;
  }

  const accidentalInformation: { cautionary?: boolean } = {};

  if ((!inKeySig && !inMeasureAccidentals) || contradictedInMeasureAccidentals) {
    needsAccidental = true;
  } else if (note.forceAccidental) {
    needsAccidental = true;
  } else if (note.cautionaryAccidental) {
    needsAccidental = true;
    accidentalInformation.cautionary = true;
  }

  if (needsAccidental) haystack.push(note);
  return needsAccidental ? accidentalInformation : false;
}

/**
 *
 * @param staveNote StaveNote object
 * @param noteHolder Will not contain any pending notes
 * @param notes All notes -- INCLUDING any pending (aka ghosting) notes
 */
export function styleStaveNote(
  staveNote: StaveNote,
  noteHolder: NoteHolder,
  /** May be different from NoteHolder.notes if there's a pending note */
  notes: Note[],
  rendererOptions: { selectedColor: string; ghostColor: string }
) {
  // The staveNote indexes & noteHolder indexes match -- so it's safe
  // to do an index search on noteHolder and style that index in a staveNote

  // If the whole holder has been colored:
  if (noteHolder.color && !noteHolder.isNeitherNoteNorRest()) {
    staveNote.setStyle({ fillStyle: noteHolder.color, strokeStyle: noteHolder.color });
  }

  if (noteHolder.strokeColor && !noteHolder.isNeitherNoteNorRest()) {
    staveNote.setStyle({
      fillStyle: noteHolder.color || 'black',
      strokeStyle: noteHolder.strokeColor,
    });
  }

  // If any notes have been colored:
  // Decorate any colored notes:
  notes.forEach((note, index) => {
    if (note.color) {
      staveNote.setKeyStyle(index, {
        fillStyle: note.color,
        strokeStyle: note.color,
      });
    }
  });

  // If it's editable:
  if (noteHolder.editable) {
    // style each notehead if needed...
    notes.forEach((note, index) => {
      if (noteHolder.editable && noteHolder.editable.selectedNotes.includes(note)) {
        staveNote.setKeyStyle(index, {
          fillStyle: rendererOptions.selectedColor,
          strokeStyle: rendererOptions.selectedColor,
        });
      }
      if (noteHolder.editable && noteHolder.editable.pendingNote === note) {
        staveNote.setKeyStyle(index, {
          fillStyle: rendererOptions.ghostColor,
          strokeStyle: rendererOptions.ghostColor,
        });
      }
    });

    // decorate it if it's a pending rest:
    if (noteHolder.rendersAs === 'pendingRest' || noteHolder.rendersAs === 'placeHolderRest') {
      staveNote.setStyle({
        fillStyle: rendererOptions.ghostColor,
        strokeStyle: rendererOptions.ghostColor,
      });
    }

    // decorate it if the whole thing is selected:
    const noSelectedNotes =
      noteHolder.editable.selectedNotes.length === 0 &&
      !noteHolder.editable.eventHandler.hasPoppedSelected;
    const isNoteOrRest = !noteHolder.isNeitherNoteNorRest();
    if (isNoteOrRest && noSelectedNotes && noteHolder.editable.selected) {
      staveNote.setStyle({
        fillStyle: rendererOptions.selectedColor,
        strokeStyle: rendererOptions.selectedColor,
      });
    }
  }
}
